import {FC, ReactNode} from "react"
import {ReactSVG} from "react-svg"

import riIconWarningDark from "@frontend/design/icons/ri-icon-warning-dark.svg"
import riIconWarningLight from "@frontend/design/icons/ri-icon-warning-light.svg"
import {css, cva, cx} from "@styled-system/css"
import {hstack} from "@styled-system/patterns"
import {token} from "@styled-system/tokens"

type Variant = "informational" | "warning"

const baseStyles = {
  paddingY: 12,
  paddingX: 16,
  display: "flex",
  justifyContent: "flex-start",
  gap: 8,
  alignItems: "flex-start",
  textStyle: "captionRegular",
}

const stylesOnNeutral = cva({
  base: baseStyles,
  variants: {
    variant: {
      informational: {
        backgroundColor: "background.brand.primary",
        color: "text.brand.primary",
      },
      warning: {
        border: "1px solid token(colors.border.errorSubtle)",
        backgroundColor: "background.error",
        color: "text.brand.primary",
      },
    },
  },
})

const stylesTransparent = cva({
  base: baseStyles,
  variants: {
    variant: {
      informational: {
        backgroundColor: "background.brand.solidSubtle",
        color: "text.primary.onSolid",
      },
      warning: {
        border: "1px solid token(colors.border.errorSubtle)",
        backgroundColor: "background.brand.solidSubtle",
        color: "text.primary.onSolid",
      },
    },
  },
})

interface Props {
  content: string | ReactNode
  variant?: Variant
  transparent?: boolean
  className?: string
}

/** Hex + sparkle; sparkle fill follows theme brandAccent (Bodewell yellow, RI orange, RC gold). */
const InformationalIcon: FC<{onSolid: boolean}> = ({onSolid}) => (
  <svg
    width="25"
    height="21"
    viewBox="0 0 25 21"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M18.5986 0H6.59863L0.598633 10.4993L6.59863 21H18.5986L24.5986 10.4993L18.5986 0Z"
      fill={onSolid ? "white" : "black"}
    />
    <path
      d="M12.5984 2.84763L12.3507 3.54871C11.2469 6.67414 8.81243 9.13517 5.71905 10.2491L5.02515 10.4994L5.71905 10.7497C8.81243 11.8648 11.2482 14.3246 12.3507 17.45L12.5984 18.1511L12.8462 17.45C13.9499 14.3246 16.3844 11.8635 19.4778 10.7497L20.1717 10.4994L19.4778 10.2491C16.3844 9.13386 13.9486 6.67414 12.8462 3.54871L12.5984 2.84763Z"
      fill={token("colors.brandAccent.600")}
    />
  </svg>
)

export const InformationBanner: FC<Props> = ({
  content,
  variant = "informational",
  transparent,
  className,
}) => (
  <div
    className={cx(
      hstack({
        gap: 16,
        width: "100%",
        padding: 16,
        borderRadius: 16,
        maxWidth: "100%",
      }),
      transparent ? stylesTransparent({variant}) : stylesOnNeutral({variant}),
      className,
    )}
  >
    {variant === "informational" ? (
      <InformationalIcon onSolid={!!transparent} />
    ) : (
      <ReactSVG src={transparent ? riIconWarningLight : riIconWarningDark} />
    )}
    <p
      className={css({
        textStyle: "caption",
        alignSelf: "center",
        wordBreak: "break-word",
      })}
    >
      {content}
    </p>
  </div>
)
